import React, { useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import { Condition, IHorizontalPodAutoscalerSelector } from '../model'
import Progress from 'core/components/progress/Progress'
import SummaryCard from 'k8s/components/common/entity/summary-card'
import InfoCard, { renderDetailRow } from 'k8s/components/common/entity/info-card'
import { getFieldsForCard } from 'core/components/InfoPanel'
import LabelsAndAnnotationsSection from 'k8s/components/common/entity/labels-and-annotations/LabelsAndAnnotationsSection'
import { renderAge, renderAgeFromTimestamp } from 'k8s/components/common/entity/helpers'
import Card from 'core/elements/card'
import { OwnerReferences } from '../../config-maps/details/ConfigMapsOverview'
import Grid, { GridViewColumn } from 'core/elements/grid/Grid'
import { DateAndTime } from 'core/components/listTable/cells/DateCell'
import ResourceInfo from 'app/plugins/kubevirt/components/virtual-machines/vm-details/ResourceInfo'
import Text from 'core/elements/Text'
import SimpleLink from 'core/components/SimpleLink'
import ModalForm from 'core/elements/modal/ModalForm'

const summaryRows: { key: keyof IHorizontalPodAutoscalerSelector; label: string }[] = [
  { key: 'clusterName', label: 'Cluster' },
  { key: 'namespace', label: 'Namespace' },
  { key: 'age', label: 'Age' },
]

const conditionsColumns: GridViewColumn<Condition>[] = [
  {
    key: 'type',
    label: 'Type',
    width: 'medium',
  },
  {
    key: 'status',
    label: 'Status',
  },
  {
    key: 'lastTransitionTime',
    label: 'Last Transition Time',
    CellComponent: DateAndTime,
    width: 'medium',
  },
  { key: 'reason', label: 'Reason' },
  { key: 'message', label: 'Message' },
]

const getResourceFields = (horizontalPodAutoscaler) => {
  let current = ''
  let target = ''

  if (!!horizontalPodAutoscaler?.status?.currentMetrics) {
    const resourceMetric = horizontalPodAutoscaler?.status?.currentMetrics.find(
      (metric) => metric.type === 'Resource',
    )
    if (resourceMetric) {
      current = resourceMetric.resource.current.averageUtilization
    }
  }

  if (!!horizontalPodAutoscaler?.spec?.metrics) {
    const resourceMetric = horizontalPodAutoscaler?.spec?.metrics.find(
      (metric) => metric.type === 'Resource',
    )
    if (resourceMetric) {
      target = resourceMetric.resource.target.averageUtilization
    }
  }

  return {
    Current: current,
    Target: target,
  }
}

const getMemoryFields = (horizontalPodAutoscaler) => {
  let current = ''
  let target = ''

  if (!!horizontalPodAutoscaler?.status?.currentMetrics) {
    const resourceMetric = horizontalPodAutoscaler?.status?.currentMetrics.find(
      (metric) => metric.type === 'Memory',
    )
    if (resourceMetric) {
      current = resourceMetric.resource.current.averageUtilization
    }
  }

  if (!!horizontalPodAutoscaler?.spec?.metrics) {
    const resourceMetric = horizontalPodAutoscaler?.spec?.metrics.find(
      (metric) => metric.type === 'Memory',
    )
    if (resourceMetric) {
      target = resourceMetric.resource.target.averageUtilization
    }
  }

  return {
    Current: current,
    Target: target,
  }
}

const scaleTargetFields = [
  {
    id: 'name',
    title: 'Name',
    required: true,
  },
  {
    id: 'kind',
    title: 'Kind',
  },
  {
    id: 'apiVersion',
    title: 'API Version',
  },
]

const ScaleTargetRefModal = ({ scaleTargetRef }) => {
  const [showModal, setShowModal] = useState(false)

  const item = useMemo(() => {
    return getFieldsForCard(scaleTargetFields, scaleTargetRef)
  }, [scaleTargetRef, open])

  return (
    <>
      <ModalForm
        open={showModal}
        title={`Scale Target Ref (${scaleTargetRef?.name})`}
        onClose={() => setShowModal(false)}
      >
        <div>{renderDetailRow(item)}</div>
      </ModalForm>
      <Text variant="caption1">{scaleTargetRef?.name}</Text>
      <br />
      <SimpleLink onClick={() => setShowModal(true)}>See More</SimpleLink>
    </>
  )
}

const HorizontalPodAutoscalersOverview = ({
  horizontalPodAutoscaler,
  loading,
  reload,
}: {
  horizontalPodAutoscaler: IHorizontalPodAutoscalerSelector
  loading: boolean
  reload: (refetch: boolean) => void
}) => {
  const classes = useStyles()

  const metadataFields = [
    {
      id: 'age',
      title: 'Created',
      required: true,
      render: renderAge,
    },
    {
      id: 'ownerReferences',
      title: 'Owner References',
      render: (value) => <OwnerReferences refs={value} />,
      condition: ({ ownerReferences }) => ownerReferences?.length > 0,
    },
    {
      id: 'spec.scaleTargetRef',
      title: 'Scale Target',
      render: (value) => <ScaleTargetRefModal scaleTargetRef={value} />,
    },
    {
      id: 'spec.minReplicas',
      title: 'Min Replicas',
      required: true,
    },
    {
      id: 'spec.maxReplicas',
      title: 'Max Replicas',
      required: true,
    },
    {
      id: 'status.lastScaleTime',
      title: 'Last Scale Time Replicas',
      required: true,
      render: renderAgeFromTimestamp,
    },
    {
      id: 'status.currentReplicas',
      title: 'Current Replicas',
      required: true,
    },
    {
      id: 'status.desiredReplicas',
      title: 'Desired Replicas',
      required: true,
    },
  ]

  const metadata = useMemo(() => {
    return getFieldsForCard(metadataFields, horizontalPodAutoscaler)
  }, [horizontalPodAutoscaler])

  return (
    <Progress loading={loading}>
      <div className={classes.container}>
        <div className={classes.column}>
          <SummaryCard<IHorizontalPodAutoscalerSelector>
            title={horizontalPodAutoscaler?.name}
            subtitle="Horizontal Pod Autoscaler"
            rows={summaryRows}
            data={horizontalPodAutoscaler}
          />
          <InfoCard
            items={metadata}
            title="Properties"
            footer={
              <div className={classes.footer}>
                <LabelsAndAnnotationsSection
                  entity={horizontalPodAutoscaler}
                  resourceType="horizontalPodAutoscaler"
                />
              </div>
            }
          />
        </div>
        <div className={classes.column}>
          <Card title="Status Conditions" withCustomBody>
            <Grid
              uniqueIdentifier="lastTransitionTime"
              data={horizontalPodAutoscaler?.status?.conditions || []}
              columns={conditionsColumns}
              loading={loading}
              compact
              disableToolbar
            />
          </Card>
          <Card title="Status Conditions" withCustomBody>
            <ResourceInfo type="Resource" fields={getResourceFields(horizontalPodAutoscaler)} />
            <ResourceInfo type="Memory" fields={getMemoryFields(horizontalPodAutoscaler)} />
          </Card>
        </div>
      </div>
    </Progress>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'grid',
    gridTemplateColumns: '633px 1fr',
    marginTop: '16px',
    gridGap: '24px',
  },
  column: {
    display: 'grid',
    gridGap: '24px',
    gridAutoFlow: 'row',
    gridAutosummaryRows: 'max-content',
  },
  footer: {
    margin: theme.spacing(1),
  },
  copyClipboard: {
    '& .copy': {
      color: theme.components.typography.passive,
    },
  },
}))

export default HorizontalPodAutoscalersOverview
